"use client";

// Dependency-free export to a CSV that Excel opens natively (UTF-8 BOM).

type Cell = string | number | null | undefined;

function escapeCsv(value: Cell): string {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function downloadCsv(filename: string, headers: string[], rows: Cell[][]) {
  const lines = [headers.map(escapeCsv).join(",")];
  for (const row of rows) lines.push(row.map(escapeCsv).join(","));
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function dateStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

// Walk a paginated endpoint to collect every matching row (bounded for safety).
export async function fetchAllPages<T>(
  fetchPage: (offset: number, limit: number) => Promise<{ items: T[]; hasMore: boolean }>,
  opts: { pageSize?: number; max?: number } = {}
): Promise<T[]> {
  // 200 meant a 50,000-row export was 250 SEQUENTIAL requests, each with a deeper OFFSET than
  // the last (at offset 49,800 the database skips 49,800 rows to return 200) — so the cost grew
  // with the square of the export. 1,000 is still a modest response and cuts that to ~50.
  const pageSize = opts.pageSize ?? 1000;
  const max = opts.max ?? 50000;
  const all: T[] = [];
  let offset = 0;
  for (let i = 0; i < 1000; i++) {
    const { items, hasMore } = await fetchPage(offset, pageSize);
    all.push(...items);
    if (!hasMore || items.length === 0 || all.length >= max) break;
    // Advance by what the server ACTUALLY returned, never by what we asked for. Endpoints clamp
    // `limit` to their own ceiling (several cap at 100-500), so advancing by pageSize would step
    // straight over every row the server withheld and silently produce an export with holes in it.
    offset += items.length;
  }
  return all.slice(0, max);
}
